import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  api,
  CreateFolderBody,
  CreateLinkBody,
  ReorderNavigationElementsBodyInner,
  UpdateNavigationElementBody,
  UpdatePageLayoutBodyInner,
} from "@/lib/api";
import { queryKeys as adminQueryKeys, useAdminDashboard } from "@/features/admin/hooks";
import { useInvalidateSelectedDashboard } from "@/features/admin/hooks/use-invalidate-selected-dashboard";

import { queryKeys } from "./";

export const useCreateFolderMutation = () => {
  const queryClient = useQueryClient();
  const invalidateCurrentDashboard = useInvalidateSelectedDashboard();

  return useMutation({
    mutationFn: ({ dashboardId, body }: { dashboardId: string; body: CreateFolderBody }) =>
      api.navigationElements.createFolderNavigationElement(dashboardId, body),
    onSuccess: (_, { dashboardId }) => {
      queryClient.invalidateQueries({
        queryKey: adminQueryKeys.dashboardElements(dashboardId),
      });
      invalidateCurrentDashboard();
    },
  });
};

export const useCreatePageMutation = () => {
  const queryClient = useQueryClient();
  const invalidateCurrentDashboard = useInvalidateSelectedDashboard();

  return useMutation({
    mutationFn: ({ dashboardId, body }: { dashboardId: string; body: CreateLinkBody }) =>
      api.navigationElements.createLinkNavigationElement(dashboardId, body),
    onSuccess: (_, { dashboardId }) => {
      queryClient.invalidateQueries({
        queryKey: adminQueryKeys.dashboardElements(dashboardId),
      });
      invalidateCurrentDashboard();
    },
  });
};

export const useDeleteNavigationElementMutation = () => {
  const queryClient = useQueryClient();
  const dashboard = useAdminDashboard(false);
  const invalidateCurrentDashboard = useInvalidateSelectedDashboard();

  return useMutation({
    mutationFn: (navigationElementId: string) =>
      api.navigationElements.deleteNavigationElement(navigationElementId),
    onSuccess: () => {
      if (dashboard?.id) {
        queryClient.invalidateQueries({
          queryKey: adminQueryKeys.dashboardElements(dashboard.id),
        });
      }
      invalidateCurrentDashboard();
    },
  });
};

export const useUpdateNavigationElementMutation = () => {
  const queryClient = useQueryClient();
  const invalidateCurrentDashboard = useInvalidateSelectedDashboard();

  return useMutation({
    mutationFn: ({
      navigationElementId,
      data,
    }: {
      navigationElementId: string;
      dashboardId: string;
      data: UpdateNavigationElementBody;
    }) => api.navigationElements.updateNavigationElement(navigationElementId, data),
    onSuccess: (_, { dashboardId }) => {
      queryClient.invalidateQueries({
        queryKey: adminQueryKeys.dashboardElements(dashboardId),
      });
      invalidateCurrentDashboard();
    },
  });
};

export const useUpdateNavigationElementsOrderMutation = () => {
  const queryClient = useQueryClient();
  const invalidateCurrentDashboard = useInvalidateSelectedDashboard();

  return useMutation({
    mutationFn: ({
      dashboardId,
      data,
    }: {
      dashboardId: string;
      data: Array<ReorderNavigationElementsBodyInner>;
    }) => api.navigationElements.reorderNavigationElementsForDashboard(dashboardId, data),
    onSuccess: (_, { dashboardId }) => {
      queryClient.invalidateQueries({
        queryKey: adminQueryKeys.dashboardElements(dashboardId),
      });
      invalidateCurrentDashboard();
    },
  });
};

export const useUpdatePageLayoutMutation = () => {
  const queryClient = useQueryClient();
  const invalidateCurrentDashboard = useInvalidateSelectedDashboard();

  return useMutation({
    mutationFn: ({
      pageId,
      data,
    }: {
      pageId: string;
      data: Array<UpdatePageLayoutBodyInner>;
    }) => api.pages.updatePageLayout(pageId, data),
    onSuccess: (_, { pageId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.page(pageId) });
      invalidateCurrentDashboard();
    },
  });
};
